import React, { useEffect, useState } from 'react'
import { Stethoscope, Router, Wifi, Radio, Clock } from 'lucide-react'
import api from '../api'
import { Modal, ModalHeader, Alert, Badge } from './ui'

// Diekstrak dari pages/Perangkat.jsx (2026-07-24) supaya bisa dipakai jg dari Daftar Pelanggan
// (PelangganKontak.jsx) -- cross-link "gambaran lengkap 1 pelanggan" tanpa perlu loncat menu,
// TANPA menggabungkan kedua halaman jadi satu (sumber data beda arsitektur, lihat komentar
// enrichDenganBilling di server/routes/genieacs.js). Backend-nya (routes/diagnose.js) tidak berubah.

export function rxPowerColor(rx) {
  const power = parseFloat(rx)
  if (isNaN(power)) return 'var(--text-muted)'
  if (power > -25) return 'var(--success)'
  if (power > -27) return 'var(--warning)'
  return 'var(--danger)'
}

const eventTone = {
  timeout: 'danger',
  terminating: 'danger',
  disconnected: 'warning',
  connected: 'success',
  authenticated: 'success',
  other: 'neutral'
}

export function Info({ label, value, color }) {
  return (
    <div style={{ background: '#fafafc', border: '1px solid var(--border)', borderRadius: 9, padding: '9px 12px' }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ fontWeight: 600, color: color || 'var(--text)' }}>{value ?? 'N/A'}</div>
    </div>
  )
}

export function Section({ title, icon: Icon, children }) {
  return (
    <div style={{ marginBottom: 20, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
      <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 7 }}>
        {Icon && <Icon size={14} color="var(--text-muted)" />}{title}
      </h3>
      {children}
    </div>
  )
}

function DiagGrid({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13 }}>{children}</div>
}

export default function DiagnostikModal({ username, onClose }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    setLoading(true); setError(''); setData(null)
    api.get(`/diagnose/${encodeURIComponent(username)}`)
      .then(res => { if (!cancelled) setData(res.data) })
      .catch(e => { if (!cancelled) setError(e.response?.data?.message || 'Gagal menjalankan diagnostik') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [username])

  return (
    <Modal onClose={onClose} width={680}>
      <ModalHeader title="Diagnostik Pelanggan" subtitle={username} onClose={onClose} />

      {loading && <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-faint)' }}>Memuat diagnostik...</div>}
      {error && <Alert type="error">{error}</Alert>}

      {data && (
        <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
          {data.summary?.length > 0 && (
            <Section title="Ringkasan" icon={Stethoscope}>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.7 }}>
                {data.summary.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </Section>
          )}

          <Section title="Sesi PPPoE (Mikrotik)" icon={Router}>
            {data.mikrotik.connected ? (
              <DiagGrid>
                <Info label="IP Aktif" value={data.mikrotik.session.address} />
                <Info label="Caller-ID MAC" value={data.mikrotik.session.callerId} />
                <Info label="Uptime Sesi" value={data.mikrotik.session.uptime} />
              </DiagGrid>
            ) : (
              <p style={{ fontSize: 13, color: 'var(--danger)' }}>Tidak ada sesi PPPoE aktif — pelanggan sedang offline.</p>
            )}
          </Section>

          <Section title="Perangkat ONT (GenieACS)" icon={Wifi}>
            {data.genieacs.found ? (
              <DiagGrid>
                <Info label="Model / SN" value={`${data.genieacs.model || '-'} / ${data.genieacs.serialNumber || '-'}`} />
                <Info label="Redaman (RX Power)" value={data.genieacs.rxPower != null ? `${data.genieacs.rxPower} dBm` : 'N/A'}
                  color={rxPowerColor(data.genieacs.rxPower)} />
                <Info label="Suhu" value={data.genieacs.temperature ? `${data.genieacs.temperature}°C` : 'N/A'} />
                <Info label="Uptime Perangkat" value={data.genieacs.deviceUptime || 'N/A'} />
                <Info label="Uptime PPPoE" value={data.genieacs.pppUptime || 'N/A'} />
                <Info label="WiFi Terhubung" value={data.genieacs.activeDevices ?? 'N/A'} />
              </DiagGrid>
            ) : (
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Tidak ditemukan di GenieACS untuk username ini.</p>
            )}
          </Section>

          <Section title="Sinyal Upstream (OLT)" icon={Radio}>
            {data.olt.found ? (
              <>
                <div style={{ marginBottom: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Badge tone="brand">OLT {data.olt.source === 'hioso' ? 'Hioso' : data.olt.source === 'hsgq' ? 'HSGQ' : data.olt.source}</Badge>
                  <Badge tone={/up|online/i.test(data.olt.status) ? 'success' : 'danger'}>{data.olt.status}</Badge>
                  <Badge tone="neutral">Port {data.olt.port}</Badge>
                  {data.olt.approximate && <Badge tone="warning">MAC cocok perkiraan (selisih {data.olt.distance})</Badge>}
                </div>
                <DiagGrid>
                  <Info label="RX Power (OLT terima dari ONU)" value={data.olt.rxPower != null ? `${data.olt.rxPower} dBm` : 'N/A'}
                    color={rxPowerColor(data.olt.rxPower)} />
                  <Info label="TX Power (ONU)" value={data.olt.txPower != null ? `${data.olt.txPower} dBm` : 'N/A'} />
                  {data.olt.temperature != null && <Info label="Suhu" value={`${data.olt.temperature}°C`} />}
                  {(data.olt.regAt || data.olt.lastRegAt) && <Info label="Terakhir Terdaftar di OLT" value={data.olt.regAt || data.olt.lastRegAt} />}
                </DiagGrid>
              </>
            ) : (
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{data.olt.error || 'ONU tidak ditemukan di OLT manapun.'}</p>
            )}
          </Section>

          <Section title="Riwayat Koneksi Terkini (buffer log Mikrotik, ±4 jam)" icon={Clock}>
            {data.recentEvents.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Tidak ada kejadian dalam buffer log saat ini.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <tbody>
                  {data.recentEvents.map((e, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '7px 10px 7px 0', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>{e.time}</td>
                      <td style={{ padding: '7px 10px', width: 100 }}><Badge tone={eventTone[e.type] || 'neutral'}>{e.type}</Badge></td>
                      <td style={{ padding: '7px 0' }}>{e.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>
        </div>
      )}
    </Modal>
  )
}
